import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import RecetaModal from './RecetaModal';

const FILTROS = [
  { cod: '', label: 'Todas' },
  { cod: 'hoy', label: 'Hoy' },
  { cod: '7dias', label: 'Últimos 7 días' },
  { cod: 'mes', label: 'Este mes' },
];

export default function RecetasMedicas() {
  const [sp] = useSearchParams();
  // Si viene desde el odontograma (u otra pantalla) con el diagnóstico
  // ya elegido, se precarga el formulario de nueva receta directamente.
  const codPacientePrefill = sp.get('paciente') || '';
  const ciePrefill  = sp.get('cie') || '';
  const descPrefill = sp.get('desc') || '';

  const [recetas, setRecetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [filtro, setFiltro] = useState('');
  const [msg, setMsg] = useState('');

  // null = modal cerrado; 'nueva' = nueva receta; objeto receta = editar
  const [modalAbierto, setModalAbierto] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (buscar.trim()) params.set('buscar', buscar.trim());
      if (filtro) params.set('filtro', filtro);
      const { data } = await api.get(`/recetas?${params.toString()}`);
      setRecetas(data);
    } catch (e) {
      setMsg('✕ No se pudieron cargar las recetas');
    } finally {
      setLoading(false);
    }
  }, [buscar, filtro]);

  useEffect(() => {
    const t = setTimeout(cargar, 300);
    return () => clearTimeout(t);
  }, [cargar]);

  useEffect(() => {
    if (codPacientePrefill) setModalAbierto('nueva');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirEditar = async receta => {
    setMsg('');
    try {
      const { data } = await api.get(`/recetas/${receta.COD}`);
      setModalAbierto(data);
    } catch (e) {
      setMsg('✕ No se pudo cargar la receta para editar');
    }
  };

  const alGuardar = () => {
    setModalAbierto(null);
    setMsg('✓ Receta guardada');
    cargar();
  };

  const eliminar = async receta => {
    if (!window.confirm(`¿Anular la receta de ${receta.PACIENTE_NOMBRE}?`)) return;
    try {
      await api.delete(`/recetas/${receta.COD}`);
      setMsg('✓ Receta anulada');
      cargar();
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'No se pudo anular'));
    }
  };

  const verPdf = receta => {
    api.get(`/recetas/${receta.COD}/pdf`, { responseType: 'blob' }).then(({ data }) => {
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    }).catch(() => setMsg('✕ No se pudo generar el PDF'));
  };

  return (
    <>
      <style>{css}</style>
      {msg && <div className={`rm-msg ${msg.startsWith('✓') ? 'ok' : 'err'}`}>{msg}</div>}

      <div className="rm-page">
        <div className="rm-hdr">
          <div>
            <div className="rm-hdr-title">Recetas médicas</div>
            <div className="rm-hdr-sub">Normativa ACESS y control farmacológico</div>
          </div>
          <button className="btn-mo-pri" onClick={() => setModalAbierto('nueva')}>
            <i className="ti ti-plus" aria-hidden="true"></i> Nueva receta
          </button>
        </div>

        <div className="rm-search">
          <i className="ti ti-search" aria-hidden="true"></i>
          <input placeholder="Buscar por paciente, cédula o diagnóstico..."
            value={buscar} onChange={e => setBuscar(e.target.value)} />
        </div>

        <div className="rm-filtros">
          {FILTROS.map(f => (
            <button key={f.cod} className={`rm-filtro ${filtro === f.cod ? 'act' : ''}`}
              onClick={() => setFiltro(f.cod)}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? <div className="spin-wrap"><div className="sp"/></div> : (
          recetas.length === 0 ? (
            <div className="empty-hc">No hay recetas que coincidan con la búsqueda</div>
          ) : (
            <div className="rm-lista">
              {recetas.map(r => (
                <div key={r.COD} className="rm-card">
                  <div className="rm-card-top">
                    <div className="rm-card-pac">
                      <i className="ti ti-file-text" aria-hidden="true"></i>
                      <div>
                        <div className="rm-card-nombre">{r.PACIENTE_NOMBRE}</div>
                        <div className="rm-card-sub">
                          ID: {r.PACIENTE_IDENTIFICACION || '—'} · {r.FECHA?.slice(0, 10)}
                        </div>
                      </div>
                    </div>
                    <span className="rm-card-badge">{r.totalMedicamentos} medicamento{r.totalMedicamentos === 1 ? '' : 's'}</span>
                  </div>

                  {(r.DIAGNOSTICO_CIE || r.DIAGNOSTICO_DESC) && (
                    <div className="rm-card-dx">
                      {r.DIAGNOSTICO_CIE && <span className="rm-card-cie">{r.DIAGNOSTICO_CIE}</span>}
                      <span>{r.DIAGNOSTICO_DESC}</span>
                    </div>
                  )}

                  <div className="rm-card-acciones">
                    <button className="btn-mo-sec" onClick={() => abrirEditar(r)}>
                      <i className="ti ti-edit" aria-hidden="true"></i> Editar
                    </button>
                    <button className="btn-mo-sec" onClick={() => verPdf(r)}>
                      <i className="ti ti-file-download" aria-hidden="true"></i> PDF
                    </button>
                    <button className="rm-card-del" onClick={() => eliminar(r)} title="Anular">
                      <i className="ti ti-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {modalAbierto && (
        <RecetaModal
          receta={modalAbierto === 'nueva' ? null : modalAbierto}
          codPacienteInicial={modalAbierto === 'nueva' ? codPacientePrefill : undefined}
          cieInicial={modalAbierto === 'nueva' ? ciePrefill : undefined}
          descInicial={modalAbierto === 'nueva' ? descPrefill : undefined}
          onGuardado={alGuardar}
          onCerrar={() => setModalAbierto(null)}
        />
      )}
    </>
  );
}

const css = `
.rm-msg { padding:8px 14px; border-radius:8px; font-size:12px; margin-bottom:8px; }
.rm-msg.ok  { background:var(--green-lt); color:var(--green); border:1px solid var(--green); }
.rm-msg.err { background:var(--red-lt);   color:var(--red);   border:1px solid var(--red); }

.rm-page { display:flex; flex-direction:column; gap:14px; }
.rm-hdr { display:flex; align-items:center; justify-content:space-between; }
.rm-hdr-title { font-size:16px; font-weight:700; color:var(--text); }
.rm-hdr-sub { font-size:12px; color:var(--text3); margin-top:2px; }

.rm-search { display:flex; align-items:center; gap:8px; border:1px solid var(--border); border-radius:9px; padding:9px 12px; background:var(--surface); }
.rm-search input { flex:1; border:none; outline:none; background:transparent; font-size:13px; color:var(--text); }
.rm-search i { color:var(--text3); }

.rm-filtros { display:flex; gap:6px; flex-wrap:wrap; }
.rm-filtro { padding:6px 13px; border-radius:20px; border:1px solid var(--border); background:var(--surface); font-size:12px; color:var(--text2); cursor:pointer; }
.rm-filtro.act { background:var(--navy); border-color:var(--navy); color:#fff; }

.rm-lista { display:flex; flex-direction:column; gap:10px; }
.rm-card { border:1px solid var(--border); border-radius:12px; padding:14px; background:var(--surface); }
.rm-card-top { display:flex; justify-content:space-between; align-items:flex-start; gap:10px; }
.rm-card-pac { display:flex; gap:10px; align-items:flex-start; }
.rm-card-pac i { font-size:20px; color:var(--text3); margin-top:2px; }
.rm-card-nombre { font-size:13.5px; font-weight:700; color:var(--text); }
.rm-card-sub { font-size:11.5px; color:var(--text3); margin-top:1px; }
.rm-card-badge { font-size:10.5px; padding:3px 9px; border-radius:20px; background:var(--blue-lt); color:var(--navy); white-space:nowrap; }
.rm-card-dx { display:flex; gap:6px; align-items:center; font-size:12px; color:var(--text2); margin-top:10px; }
.rm-card-cie { font-size:10.5px; padding:2px 7px; border-radius:5px; background:var(--bg); color:var(--text2); font-weight:600; }
.rm-card-acciones { display:flex; gap:8px; margin-top:12px; }
.rm-card-acciones .btn-mo-sec { flex:1; font-size:12px; padding:7px; }
.rm-card-del { background:none; border:1px solid var(--red-lt); color:var(--red); border-radius:8px; padding:7px 11px; cursor:pointer; }
.rm-card-del:hover { background:var(--red-lt); }

.rm-modal-overlay { position:fixed; inset:0; background:rgba(20,25,35,.45); display:flex; align-items:center; justify-content:center; z-index:100; padding:16px; }
.rm-modal { background:var(--surface); border-radius:14px; width:100%; max-width:480px; max-height:88vh; overflow-y:auto; }
.rm-modal-hdr { padding:14px 18px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; font-size:14px; font-weight:700; color:var(--text); position:sticky; top:0; background:var(--surface); }
.rm-modal-body { padding:16px 18px; }
.rm-form-row { display:grid; grid-template-columns:1fr 1.4fr; gap:8px; }

.rm-linea { border:1px solid var(--border); border-radius:9px; padding:10px; margin-bottom:8px; display:flex; flex-direction:column; gap:6px; background:var(--bg); }
.rm-linea-top { display:flex; gap:6px; align-items:center; }
.rm-linea-top .form-control { flex:1; }
.rm-linea-del { background:none; border:none; color:var(--text3); cursor:pointer; padding:4px; flex-shrink:0; }
.rm-linea-del:hover { color:var(--red); }
.rm-linea-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:6px; }
`;
